import { z } from 'zod';

const hexColorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9A-Fa-f]{6}$/)
  .transform((value) => value.toUpperCase());

export const runtimeNetworkBrandDetailsSchema = z.looseObject({
  logoUrl: z.string().trim().min(1).nullish(),
  logoLightUrl: z.string().trim().min(1).nullish(),
  accent: hexColorSchema.nullish(),
  accentStrong: hexColorSchema.nullish(),
  sidebar: hexColorSchema.nullish(),
});

export type RuntimeNetworkBrandDetails = z.infer<typeof runtimeNetworkBrandDetailsSchema>;

export interface ResolvedNetworkBranding {
  displayName: string;
  logoUrl: string;
  logoLightUrl: string;
}

const runtimeBrandProperties = [
  '--shigo-highlight',
  '--shigo-highlight-foreground',
  '--shigo-highlight-strong',
  '--shigo-highlight-strong-foreground',
  '--primary',
  '--primary-foreground',
  '--ring',
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--sidebar-accent',
  '--sidebar-accent-foreground',
  '--sidebar-accent-hover',
  '--sidebar-ring',
  '--chart-1',
  '--chart-6',
  '--sidebar',
  '--sidebar-foreground',
] as const;

export function parseRuntimeNetworkBrandDetails(value: unknown): RuntimeNetworkBrandDetails | null {
  const result = runtimeNetworkBrandDetailsSchema.safeParse(value);
  return result.success ? result.data : null;
}

export function resolveNetworkBranding(
  brandDetails: RuntimeNetworkBrandDetails | null | undefined,
  fallback: ResolvedNetworkBranding,
): ResolvedNetworkBranding {
  if (!brandDetails) return fallback;
  return {
    displayName: fallback.displayName,
    logoUrl: brandDetails.logoUrl ?? fallback.logoUrl,
    logoLightUrl: brandDetails.logoLightUrl ?? fallback.logoLightUrl,
  };
}

export function applyRuntimeNetworkBranding(
  root: HTMLElement,
  brandDetails: RuntimeNetworkBrandDetails | null | undefined,
) {
  clearRuntimeNetworkBranding(root);
  if (!brandDetails) return;

  const properties: Partial<Record<(typeof runtimeBrandProperties)[number], string>> = {};

  if (brandDetails.accent) {
    properties['--shigo-highlight'] = brandDetails.accent;
    properties['--shigo-highlight-foreground'] = readableBrandForeground(brandDetails.accent);
    properties['--ring'] = brandDetails.accent;
    properties['--sidebar-ring'] = brandDetails.accent;
  }
  if (brandDetails.accentStrong) {
    const foreground = readableBrandForeground(brandDetails.accentStrong);
    properties['--shigo-highlight-strong'] = brandDetails.accentStrong;
    properties['--shigo-highlight-strong-foreground'] = foreground;
    properties['--primary'] = brandDetails.accentStrong;
    properties['--primary-foreground'] = foreground;
    properties['--sidebar-primary'] = brandDetails.accentStrong;
    properties['--sidebar-primary-foreground'] = foreground;
  }

  // Preserve chart overrides only for payloads accepted by the previous complete-brand contract.
  if (
    brandDetails.logoUrl &&
    brandDetails.logoLightUrl &&
    brandDetails.accent &&
    brandDetails.accentStrong &&
    brandDetails.sidebar !== undefined
  ) {
    properties['--chart-1'] = brandDetails.accent;
    properties['--chart-6'] = brandDetails.accentStrong;
  }

  if (brandDetails.sidebar) {
    const sidebarForeground = readableBrandForeground(brandDetails.sidebar);
    properties['--sidebar'] = brandDetails.sidebar;
    properties['--sidebar-foreground'] = sidebarForeground;
    properties['--sidebar-accent'] =
      `color-mix(in srgb, ${brandDetails.accent ?? 'var(--shigo-highlight)'} 12%, ${brandDetails.sidebar})`;
    properties['--sidebar-accent-foreground'] = sidebarForeground;
    properties['--sidebar-accent-hover'] =
      `color-mix(in srgb, ${brandDetails.accent ?? 'var(--shigo-highlight)'} 18%, ${brandDetails.sidebar})`;
    root.dataset.networkSidebar = 'custom';
  }

  for (const [property, value] of Object.entries(properties)) {
    if (value) root.style.setProperty(property, value);
  }
  root.dataset.networkBrand = 'custom';
}

export function clearRuntimeNetworkBranding(root: HTMLElement) {
  for (const property of runtimeBrandProperties) root.style.removeProperty(property);
  delete root.dataset.networkBrand;
  delete root.dataset.networkSidebar;
}

export function readableBrandForeground(background: string) {
  const luminance = relativeLuminance(background);
  const darkLuminance = relativeLuminance('#18181B');
  const darkContrast =
    (Math.max(luminance, darkLuminance) + 0.05) / (Math.min(luminance, darkLuminance) + 0.05);
  const whiteContrast = 1.05 / (luminance + 0.05);
  return darkContrast > whiteContrast ? '#18181B' : '#FFFFFF';
}

function relativeLuminance(color: string) {
  const linearChannel = (offset: number) => {
    const channel = Number.parseInt(color.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linearChannel(1) + 0.7152 * linearChannel(3) + 0.0722 * linearChannel(5);
}
